/**
 * ProKids Talent — API Client
 * Centraliza todas las llamadas al backend
 */

const API_URL = process.env.REACT_APP_API_URL ?? '';

// ─────────────────────────────────────────────
// HTTP Helper
// ─────────────────────────────────────────────
async function request(path, options = {}) {
  const token = localStorage.getItem('prokids_token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_URL}${path}`, { ...options, headers });

  // Auto-logout si el token expiró
  if (response.status === 401) {
    const refreshToken = localStorage.getItem('prokids_refresh_token');
    if (refreshToken) {
      try {
        const refreshed = await fetch(`${API_URL}/api/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (refreshed.ok) {
          const data = await refreshed.json();
          localStorage.setItem('prokids_token', data.token);
          // Reintentar con el nuevo token
          return request(path, options);
        }
      } catch (_) {}
    }
    localStorage.removeItem('prokids_token');
    localStorage.removeItem('prokids_refresh_token');
  }

  if (!response.ok && response.status !== 204) {
    const error = await response.json().catch(() => ({ error: 'Error de red' }));
    throw new Error(error.message || error.error || `HTTP ${response.status}`);
  }

  if (response.status === 204) return null;
  return response.json();
}

// ─────────────────────────────────────────────
// Auth API
// ─────────────────────────────────────────────
export const authApi = {
  register: (data) =>
    request('/api/v1/auth/register', { method: 'POST', body: JSON.stringify(data) }),

  login: (email, password) =>
    request('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  me: () => request('/api/v1/auth/me'),

  forgotPassword: (email) =>
    request('/api/v1/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
};

// ─────────────────────────────────────────────
// Public API (landing page)
// ─────────────────────────────────────────────
export const publicApi = {
  getMetrics: () => request('/api/v1/public/metrics'),

  getSponsors: () => request('/api/v1/public/sponsors'),

  getTestimonials: (lang = 'es') =>
    request(`/api/v1/public/testimonials?lang=${lang}`),

  getFeaturedVideos: () => request('/api/v1/public/elearning/featured'),

  getElearningCategories: () => request('/api/v1/public/elearning/categories'),
};

// ─────────────────────────────────────────────
// Contact API
// ─────────────────────────────────────────────
export const contactApi = {
  send: (data) =>
    request('/api/v1/contact', { method: 'POST', body: JSON.stringify(data) }),
};

// ─────────────────────────────────────────────
// Players API
// ─────────────────────────────────────────────
export const playersApi = {
  register: (data) =>
    request('/api/v1/players', { method: 'POST', body: JSON.stringify(data) }),

  getPublicProfile: (slug) => request(`/api/v1/players/${slug}`),

  getMyPlayers: () => request('/api/v1/players/me'),

  getDirectory: (filters = {}) => {
    const params = new URLSearchParams(
      Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== undefined && v !== '')
      )
    ).toString();
    return request(`/api/v1/players${params ? `?${params}` : ''}`);
  },
};

// ─────────────────────────────────────────────
// Subscriptions API
// ─────────────────────────────────────────────
export const subscriptionsApi = {
  getPlans: () => request('/api/v1/subscriptions/plans'),

  createCheckout: (plan, billingCycle, gateway = 'STRIPE') =>
    request('/api/v1/subscriptions/checkout', {
      method: 'POST',
      body: JSON.stringify({ plan, billingCycle, gateway }),
    }),

  getMySubscription: () => request('/api/v1/subscriptions/me'),

  getBillingPortal: () => request('/api/v1/subscriptions/portal'),

  pause: (reason) =>
    request('/api/v1/subscriptions/pause', {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  resume: () => request('/api/v1/subscriptions/resume', { method: 'POST' }),

  cancel: (reason, feedback) =>
    request('/api/v1/subscriptions/cancel', {
      method: 'POST',
      body: JSON.stringify({ reason, feedback }),
    }),
};

// ─────────────────────────────────────────────
// E-Learning API
// ─────────────────────────────────────────────
export const elearningApi = {
  getCourses: () => request('/api/v1/elearning/courses'),

  getCourseDetail: (id) => request(`/api/v1/elearning/courses/${id}`),

  getVideoStreamUrl: (videoId) =>
    request(`/api/v1/elearning/videos/${videoId}/stream`),

  recordView: (videoId) =>
    request(`/api/v1/elearning/videos/${videoId}/view`, { method: 'POST' }),
};

// ─────────────────────────────────────────────
// Scouts API
// ─────────────────────────────────────────────
export const scoutsApi = {
  getDirectory: (filters = {}) => {
    const params = new URLSearchParams(
      Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== undefined && v !== '')
      )
    ).toString();
    return request(`/api/v1/scouts/players${params ? `?${params}` : ''}`);
  },

  getPlayerProfile: (slug) => request(`/api/v1/scouts/players/${slug}`),
};

// ─────────────────────────────────────────────
// Token helpers
// ─────────────────────────────────────────────
export function saveTokens({ token, refreshToken }) {
  if (token) localStorage.setItem('prokids_token', token);
  if (refreshToken) localStorage.setItem('prokids_refresh_token', refreshToken);
}

export function clearTokens() {
  localStorage.removeItem('prokids_token');
  localStorage.removeItem('prokids_refresh_token');
}

export function isLoggedIn() {
  return !!localStorage.getItem('prokids_token');
}

export default { authApi, publicApi, contactApi, playersApi, subscriptionsApi, elearningApi, scoutsApi };
